import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LockOutlined } from '@ant-design/icons';
import { Button, Select, message } from 'antd';
import {
  getSalaryManagers,
  setSalaryManagers,
} from '@/services/compensation.service';

const sameSet = (a = [], b = []) =>
  a.length === b.length && [...a].sort().join() === [...b].sort().join();

const toOptions = (list = []) =>
  list.map((p) => ({
    value: String(p._id),
    label: p.email && p.email !== p.name ? `${p.name} (${p.email})` : p.name,
  }));

/**
 * "Manager salaries" on the company's People and access tile: the employees
 * whose pay the people on "Who sees salaries" read as not available, and who
 * sees it anyway. Saved on its own, apart from the company form. HLB only: for
 * anyone else the server refuses and the card is not shown.
 */
export default function SalaryManagersCard() {
  const { t } = useTranslation('translations');
  const tc = (key, opts) => t(`compensation.${key}`, opts);
  const { companyId } = useParams();
  const queryClient = useQueryClient();
  const queryKey = ['salary-managers', companyId];

  const { data, isError, isLoading } = useQuery({
    queryKey,
    queryFn: () => getSalaryManagers(companyId),
    enabled: !!companyId,
    retry: false,
  });
  const [managers, setManagers] = useState([]);
  const [viewers, setViewers] = useState([]);
  useEffect(() => {
    if (data) {
      setManagers(data.managers || []);
      setViewers(data.viewers || []);
    }
  }, [data]);

  const employeeOptions = useMemo(
    () => toOptions(data?.employeeOptions),
    [data?.employeeOptions]
  );
  const peopleOptions = useMemo(
    () => toOptions(data?.peopleOptions),
    [data?.peopleOptions]
  );

  const save = useMutation({
    mutationFn: () =>
      setSalaryManagers(companyId, { managerIds: managers, viewerIds: viewers }),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKey, next);
      queryClient.invalidateQueries({ queryKey: ['company-compensation', companyId] });
      message.success(tc('saved'));
    },
    onError: (error) =>
      message.error(error?.response?.data?.message || tc('saveFailed')),
  });

  if (!companyId || isError) return null;

  const dirty =
    !!data &&
    (!sameSet(managers, data.managers) || !sameSet(viewers, data.viewers));

  return (
    <section className="rounded-md border bg-zinc-50 px-4 py-4">
      <div className="mb-1 flex items-center gap-2 text-sm font-bold">
        <LockOutlined />
        {tc('managersTitle')}
      </div>
      <div className="mb-3 text-[13px] text-zinc-500">{tc('managersHint')}</div>
      <div className="grid gap-3">
        <div>
          <div className="mb-1 text-[13px] font-medium">{tc('managersLabel')}</div>
          <Select
            mode="multiple"
            allowClear
            showSearch
            className="w-full"
            loading={isLoading}
            options={employeeOptions}
            optionFilterProp="label"
            placeholder={tc('managersPlaceholder')}
            value={managers}
            onChange={setManagers}
            // Saved on its own, so the company form's read-only mode (which
            // disables every field under it) does not apply.
            disabled={false}
          />
        </div>
        <div>
          <div className="mb-1 text-[13px] font-medium">{tc('managerViewersLabel')}</div>
          <Select
            mode="multiple"
            allowClear
            showSearch
            className="w-full"
            loading={isLoading}
            options={peopleOptions}
            optionFilterProp="label"
            placeholder={tc('viewersPlaceholder')}
            value={viewers}
            onChange={setViewers}
            disabled={false}
          />
        </div>
      </div>
      {dirty && (
        <div className="mt-3 flex justify-end gap-2">
          <Button
            shape="round"
            disabled={false}
            onClick={() => {
              setManagers(data.managers || []);
              setViewers(data.viewers || []);
            }}
          >
            {tc('cancel')}
          </Button>
          <Button
            shape="round"
            type="primary"
            disabled={false}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            {tc('save')}
          </Button>
        </div>
      )}
    </section>
  );
}
